import { neutral } from '@bloomfieldrobotics/canopy';
import { Box, CSSObject, IconButton, styled, Theme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { PropsWithChildren, useState } from 'react';
import { LogoExpandableNavButton } from './buttons/LogoExpandableNavButton';
import { CloseNavIcon } from './icons/CloseNavIcon';
import { OpenNavIcon } from './icons/OpenNavIcon';
import { NavIcon } from './NavIcon';

const DRAWER_WIDTH_OPEN = 200;
const DRAWER_WIDTH_CLOSED = 60;

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH_OPEN,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: DRAWER_WIDTH_CLOSED,
});

const Drawer = styled(MuiDrawer)(({ theme }) => ({
  width: DRAWER_WIDTH_OPEN,
  display: 'flex',
  overflowX: 'hidden',
  height: '100%',
  '& .MuiDrawer-paper': {
    display: 'flex',
    overflowX: 'hidden',
    justifyContent: 'flex-end',
    padding: '13px',
    flexDirection: 'column',
    borderRight: '0.5px solid',
    borderColor: neutral[700],
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

/**
 * Expandable sidebar with the Bloomfield logo
 */
export const NavbarBase = ({ children }: PropsWithChildren) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <Drawer variant='permanent' open={isDrawerOpen}>
      <Box padding='0px 6px'>
        <LogoExpandableNavButton isExpanded={isDrawerOpen} />
      </Box>
      {children}
      <IconButton
        size='small'
        onClick={isDrawerOpen ? handleDrawerClose : handleDrawerOpen}
        sx={{ alignSelf: 'flex-end', borderRadius: 1 }}
      >
        <NavIcon color='secondary'>
          {isDrawerOpen ? <CloseNavIcon /> : <OpenNavIcon />}
        </NavIcon>
      </IconButton>
    </Drawer>
  );
};
